/**
 * Permission-based Route Guard
 * Protects routes based on specific permissions
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';

interface PermissionGuardProps {
  children: React.ReactNode;
  requiredPermissions: string[];
  fallback?: React.ReactNode;
  redirectTo?: string;
  requireAll?: boolean; // If true, user must have ALL permissions; if false, ANY permission
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({ 
  children, 
  requiredPermissions, 
  fallback,
  redirectTo = '/unauthorized',
  requireAll = false
}) => {
  const { isAuthenticated, hasPermission } = useAuthStore();

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions
  const hasPermissions = requireAll
    ? requiredPermissions.every(permission => hasPermission(permission))
    : requiredPermissions.some(permission => hasPermission(permission));

  if (!hasPermissions) {
    // Show fallback or redirect to unauthorized page
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default PermissionGuard;